/**
 * Org Param Group Registry
 *
 * Singleton registry that maps group keys to their manifests.
 * Analogous to `moduleManifestRegistry` — register once at bootstrap via
 * `registerOrgParamGroups()`, then read at runtime.
 *
 * Usage:
 *   // At bootstrap (apps/console/src/services/data-access.bootstrap.ts)
 *   registerOrgParamGroups()
 *
 *   // At runtime
 *   const manifest = orgParamGroupRegistry.getGroup('stock')
 *   const all = orgParamGroupRegistry.getAllGroups()
 */
class OrgParamGroupRegistry {
    constructor() {
        this.groups = new Map();
    }
    /**
     * Registers an org-param group manifest.
     * Overwrites any previously registered manifest with the same key and emits a
     * console warning in development — double-registration is a sign of a bootstrap
     * misconfiguration (bootstrapDataAccess already guards against it via the
     * `bootstrapped` flag).
     */
    register(manifest) {
        if (this.groups.has(manifest.key) && typeof process !== 'undefined' && process.env?.['NODE_ENV'] !== 'production') {
            console.warn(`[OrgParamGroupRegistry] Group '${manifest.key}' is already registered and will be overwritten. ` +
                'Ensure registerOrgParamGroups() is called only once at bootstrap.');
        }
        this.groups.set(manifest.key, manifest);
    }
    /** Returns the manifest for the given key, or undefined if not registered. */
    getGroup(key) {
        return this.groups.get(key);
    }
    /** Returns all registered group manifests, in registration order. */
    getAllGroups() {
        return Array.from(this.groups.values());
    }
    /** Returns true if a group manifest is registered for the given key. */
    isRegistered(key) {
        return this.groups.has(key);
    }
    /** Returns all registered group keys. */
    getAllKeys() {
        return Array.from(this.groups.keys());
    }
}
export const orgParamGroupRegistry = new OrgParamGroupRegistry();
//# sourceMappingURL=orgParamGroupRegistry.js.map